import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sendMessage, clearMessages, clearError, summarizeConversation } from '../store/slices/chatSlice';
import { logoutUser } from '../store/slices/authSlice';
import ConversationList from './ConversationList';
import SummaryModal from './SummaryModal';
import './Chat.css';

const Chat = () => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { messages, loading, error, currentConversationId, summarizing } = useSelector((state) => state.chat);
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Chat history will be fetched when a conversation is selected
    // No need to fetch on mount
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!message.trim() || loading) return;

    const messageText = message.trim();
    setMessage('');
    setIsTyping(true);

    try {
      await dispatch(sendMessage(messageText)).unwrap();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleClearChat = () => {
    if (window.confirm('Are you sure you want to clear all messages?')) {
      dispatch(clearMessages());
    }
  };

  const handleSummarize = async () => {
    if (!currentConversationId) {
      alert('Please select a conversation to summarize');
      return;
    }
    
    try {
      await dispatch(summarizeConversation(currentConversationId)).unwrap();
    } catch (error) {
      console.error('Failed to summarize conversation:', error);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="chat-layout">
      {/* Sidebar */}
      <div className={`chat-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <ConversationList />
      </div>

      {/* Main Chat Area */}
      <div className="chat-main">
        <div className="chat-container">
          <div className="chat-header">
            <div className="chat-header-left">
              <button 
                className="sidebar-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                </svg>
              </button>
              <h1>Tradomatix Chat</h1>
              {user && (
                <span className="user-info">
                  Welcome, {user.name || user.email || 'User'}
                </span>
              )}
            </div>
            <div className="chat-header-right">
              <button 
                className="btn btn-secondary btn-sm"
                onClick={handleClearChat}
                title="Clear chat history"
              >
                Clear Chat
              </button>
              <button 
                className="btn btn-danger btn-sm"
                onClick={handleLogout}
                title="Logout"
              >
                Logout
              </button>
            </div>
          </div>

          <div className="chat-messages">
            {messages.length === 0 && !loading ? (
              <div className="empty-state">
                <div className="empty-icon">💬</div>
                <h3>Select a conversation</h3>
                <p>Choose a conversation from the sidebar or start a new chat</p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`message ${msg.sender === 'user' ? 'user-message' : 'bot-message'}`}
                >
                  <div className="message-content">
                    <div className="message-text">{msg.text}</div>
                    <div className="message-time">
                      {formatTime(msg.timestamp)}
                    </div>
                  </div>
                </div>
              ))
            )}
            
            {isTyping && (
              <div className="message bot-message">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {error && (
            <div className="error-banner">
              <span>{error}</span>
              <button 
                className="error-close"
                onClick={() => dispatch(clearError())}
              >
                ×
              </button>
            </div>
          )}

          <form onSubmit={handleSendMessage} className="chat-input-container">
            <div className="chat-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="chat-input"
                disabled={loading}
              />
              {currentConversationId && (
                <button
                  type="button"
                  className="summarize-button"
                  onClick={handleSummarize}
                  disabled={summarizing}
                  title="Summarize conversation"
                >
                  {summarizing ? '...' : '📝'}
                </button>
              )}
              <button
                type="submit"
                className="send-button"
                disabled={!message.trim() || loading}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Summary Modal */}
      <SummaryModal />
    </div>
  );
};

export default Chat; 